import React from "react";
import { Table, Button } from "react-bootstrap";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import ToPeso from "../components/misc/ToPeso";
import { format, addMonths, addWeeks, addDays, addYears } from "date-fns";


function drawFormattedText(text, x, y, maxWidth, lineHeight, defaultFont, boldFont, fontSize, page, color) {
  const boldRegex = /<b>(.*?)<\/b>/g; // Regex to detect bold tags
  const stripTags = (text) => text.replace(/<b>|<\/b>/g, ""); // Function to remove tags

  // Split text by newlines first
  const paragraphs = text.split("\n");

  let currentY = y;

  for (const paragraph of paragraphs) {
    const words = paragraph.split(" "); // Split paragraph into words
    let currentLine = ""; // Current line being built
    let currentLine2 = ""; // Current line being built
    let lines = []; // Array to store formatted lines

    for (const word of words) {
      const strippedWord = stripTags(word); // Remove tags for drawing
      const testFont = word.match(boldRegex) ? boldFont : defaultFont;
      const testLine = currentLine + (currentLine ? " " : "") + strippedWord;
      const testLine2 = currentLine2 + (currentLine2 ? " " : "") + word;
      const textWidth = testFont.widthOfTextAtSize(testLine, fontSize);

      if (textWidth > maxWidth) {
        lines.push(currentLine2); // Save the current line
        currentLine = strippedWord; // Start a new line
        currentLine2 = word; // Start a new line
      } else {
        currentLine = testLine; // Add word to the current line
        currentLine2 = testLine2; // Start a new line
      }
    }

    // Push the last line
    if (currentLine) {
      lines.push(currentLine2);
    }

    // Draw each line with formatting
    for (const line of lines) {
      let offsetX = x; // Start x-coordinate
      const formattedWords = line.split(" "); // Split into words for formatting

      for (const word of formattedWords) {
        const isBold = word.match(boldRegex); // Check if word is bold
        const strippedWord = stripTags(word); // Remove tags for drawing
        const wordFont = isBold ? boldFont : defaultFont; // Apply bold font if detected

        // Draw the word
        page.drawText(strippedWord, {
          x: offsetX,
          y: currentY,
          size: fontSize,
          font: wordFont, // Correct font applied
          color: color,
        });

        // Adjust x-position for the next word
        offsetX += wordFont.widthOfTextAtSize(strippedWord + " ", fontSize);
      }

      currentY -= lineHeight; // Move down for the next line
    }

    currentY -= lineHeight; // Additional spacing between paragraphs
  }
}

// Utility function for rounding up
const roundUp = (amount) => {
  const amountString = amount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);

  if (secondDecimalPlace !== "0") {
    return parseFloat((amount + 0.01).toFixed(2));
  }

  return amount;
};

// Function to calculate due dates and amounts
const calculateDueDates = (startDate, interval, term, amount, interest, adv) => {
  const dueDates = [];
  const principalAmount = amount / term;
  const interestAmountInitial = (adv)?(amount * interest) / 100:0;
  const interestAmountMinusAdvance = (((amount * interest) / 100) * term) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / term;
  
  let currentDate = new Date(startDate);

  for (let i = 0; i < term; i++) {
    switch (interval.toLowerCase()) {
      case "monthly":
        currentDate = addDays(currentDate, 31);
        break;
      case "bimonthly": // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case "weekly":
        currentDate = addWeeks(currentDate, 1);
        break;
      case "quarterly":
        currentDate = addMonths(currentDate, 3);
        break;
      case "semiannual":
        currentDate = addMonths(currentDate, 6);
        break;
      case "yearly":
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error("Invalid interval");
    }

    dueDates.push({
      amortizationNumber: i + 1,
      amortizationDate: format(currentDate, "MM/dd/yyyy"),
      principal: roundUp(principalAmount),
      interest: roundUp(interestAmount),
      total: roundUp(principalAmount + interestAmount),
    });
  }

  return dueDates;
};




// React Component
function DocumentAffidavitIfConsentAndConformityToPPLCsContractualSavingsProgram({ applicantDetails, loanProcessingDetails }) {
  if (!applicantDetails) {
    return null;
  }
  
  const generatePDF = async (loan) => {

    const schedule = calculateDueDates(
      loan.dateGranted,
      loan.loanInterval,
      loan.loanTerm,
      loan.amountGranted,
      loan.interestRate,
      loan.advanceInterest
    );

    console.log(schedule);
    
    const existingPdfBytes = await fetch("/pdftemplates/affidavit-if-consent-and-conformity-to-PPLCs-contractual-savings-program.pdf").then(res =>
        res.arrayBuffer()
    );

    // Load PDF with pdf-lib
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Embed fonts
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Get the first page
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

  // borrower name
  firstPage.drawText(`${applicantDetails.firstName.toUpperCase()} ${applicantDetails.middleName.toUpperCase() || ""} ${applicantDetails.lastName.toUpperCase()}`, {
    x: 425,
    y: 406,
    size: 9,
    font: helveticaFontBold, 
    color: rgb(0, 0, 0)
  });

  //dates

  // Get today's date
  const today = new Date();

  // Extract day, month, and year
  const day = today.getDate();
  const month = today.toLocaleString('default', { month: 'long' }); // e.g., "January", "February"
  const year = today.getFullYear();

  firstPage.drawText(`${day}`, {
    x: 98,
    y: 502,
    size: 9,
    font: helveticaFontBold, 
    color: rgb(0, 0, 0)
  });

  firstPage.drawText(`${month} ${year}`, {
    x: 153,
    y: 502,
    size: 9,
    font: helveticaFontBold, 
    color: rgb(0, 0, 0)
  });

    const longText = `
    I, ${applicantDetails.firstName.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ")} <b>${applicantDetails.middleName.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ") || ""}</b> <b>${applicantDetails.lastName.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ")}</b> of legal age, a resident of ${applicantDetails.street.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ")}, ${applicantDetails.barangay.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ")}, ${applicantDetails.municipality.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ")}, ${applicantDetails.province.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ")} ${applicantDetails.zipCode.toUpperCase().split(" ").map((w) => `<b>${w}</b>`).join(" ")}, conforms to <b>HAYAHAY</b> <b>LENDING</b> <b>CORP's</b> Contractual Savings Program as a component requirement to avail of a loan.
    `;

    drawFormattedText(
      longText,
      37, // Starting x-coordinate
      652, // Starting y-coordinate
      538, // Max width for wrapping
      12, // Line height
      helveticaFont, // Default font
      helveticaFontBold, // Bold font
      9, // Font size
      firstPage, // The PDF page
      rgb(0, 0, 0) // Text color
    );
    

    // Save the modified PDF
    const pdfBytes = await pdfDoc.save();

    // Use FileSaver to download the PDF
    saveAs(new Blob([pdfBytes]), `Affidavit_If_Consent_And_Conformity_To_PPLCs_Contractual_Savings_Program_${applicantDetails.firstName.toUpperCase()}_${applicantDetails.lastName.toUpperCase()}_${loan.id}.pdf`);

  };

  return (
    <>
      <h3>Applicant Details</h3>
      <Table className="list-container" hover responsive>
        <tbody>
          <tr>
            <td><strong>Full Name</strong></td>
            <td>{`${applicantDetails.firstName} ${applicantDetails.middleName || ""} ${applicantDetails.lastName}`}</td>
          </tr>
          <tr>
            <td><strong>Address</strong></td>
            <td>{`${applicantDetails.street}, ${applicantDetails.barangay}, ${applicantDetails.municipality}, ${applicantDetails.province}`}</td>
          </tr>
        </tbody>
      </Table>

      <h3>Loan Processing Details</h3>
      {loanProcessingDetails.length > 0 ? (
        <Table className="list-container" hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount Granted</th>
              <th>Date Granted</th>
              <th>Interest Rate</th>
              <th>Loan Term</th>
              <th>Payment Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loanProcessingDetails.map((loan, index) => (
              <tr key={index}>
                <td>{loan.loanProcessingType}</td>
                <td><ToPeso amount={parseFloat(loan.amountGranted).toFixed(2)} /></td>
                <td>{format(new Date(loan.dateGranted), "MM/dd/yyyy")}</td>
                <td>{loan.interestRate}%</td>
                <td>{loan.loanTerm} months</td>
                <td>{loan.paymentType}</td>
                <td>
                  <Button variant="primary" onClick={() => generatePDF(loan)}>
                    Generate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No loan processing details found for this applicant.</p>
      )}
    </>
  );
}

export default DocumentAffidavitIfConsentAndConformityToPPLCsContractualSavingsProgram;
